import React from "react";
import SectionTitle from "./SectionTitle";
import DetailItem from "./DetailItem";
import { Link } from "react-router-dom";
import { cn } from "../../../../utils/classnames";
import moment from "moment";

const formatLocationBlock = (locationValue) => {
    const formattedAddress = locationValue?.formatted_address || locationValue?.label || "";
    if (!formattedAddress) return { title: "-", subtitle: "" };
    const parts = formattedAddress.split(",").map((part) => part.trim()).filter(Boolean);
    return {
        title: parts[0] || "-",
        subtitle: parts.slice(1).join(", "),
    };
};

const formatChildSeatSummaryByInfo = (bookingInfoItem) => {
    if (!Array.isArray(bookingInfoItem?.childSeat)) return "-";
    const mapped = bookingInfoItem.childSeat
        .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
        .map((seat) => {
            const seatType = String(seat?.childSeatType || "").toLowerCase();
            const label = seatType.includes("toddler") ? "Toddler Size" : "Child Size";
            return `${label}: ${seat?.childSeatCount}`;
        });
    return mapped.length ? mapped.join(" | ") : "-";
};

const MEET_ASSIST_LABELS = {
    meetAssist: "Meet & Assist",
    vipMeetAssist: "VIP Meet & Assist",
};

const TERMINAL_LABEL_MAP = {
    "terminal-1": "Terminal 1",
    "terminal-2": "Terminal 2",
    "terminal-3": "Terminal 3",
    "terminal-1-domestic": "Terminal 1 (Domestic)",
    "terminal-2-domestic": "Terminal 2 (Domestic)",
    "terminal-2-international": "Terminal 2 (International)",
    "terminal-3-international": "Terminal 3 (International)",
};

const BookingDetails = ({
    booking,
    adultCount,
    childCount,
    toddlerCount,
    infantCount,
    checkinCount,
    cabinCount,
    specialRequestText
}) => {
    const bookingInfo = Array.isArray(booking?.bookingInfo) ? booking.bookingInfo : [];
    const firstBookingInfo = bookingInfo[0] || {};
    const pickupLocationBlock = formatLocationBlock(booking?.pickupLocation);
    const dropoffLocationBlock = formatLocationBlock(booking?.destinations?.[0]);
    // Grey out a passenger/luggage type when its count is 0, dark when present.
    const countColor = (count) => (Number(count) > 0 ? "#282828" : "#8D8D8D");
    const vehiclePassengerRows = (bookingInfo.length ? bookingInfo : [firstBookingInfo]).map((item, index) => ({
        key: item?._id || item?.vehicleID?._id || `vehicle-row-${index}`,
        index,
        vehicleLabel: `Vehicle #${index + 1}`,
        vehicleName: item?.vehicleTypeName || item?.vehicleTypeID?.typename || item?.vehicleID?.vehiclename || "-",
    }));
    const isMultipleVehicle = vehiclePassengerRows.length > 1;
    const multiVehicleAddonRows = (bookingInfo.length ? bookingInfo : [firstBookingInfo]).map((item, index) => ({
        key: item?._id || item?.vehicleID?._id || `vehicle-addon-${index}`,
        vehicleLabel: `Vehicle #${index + 1}`,
        vehicleName: item?.vehicleTypeName || item?.vehicleTypeID?.typename || item?.vehicleID?.vehiclename || "-",
        childSeatText: formatChildSeatSummaryByInfo(item),
        babyStroller: item?.babyStroller || "-",
        wheelchair: item?.wheelchair || "-",
    }));
    const childSeatSummary = Array.isArray(firstBookingInfo?.childSeat)
        ? firstBookingInfo.childSeat
            .filter((item) => Number(item?.childSeatCount || 0) > 0)
            .map((item) => `${item?.childSeatType} x${item?.childSeatCount}`)
            .join(", ")
        : "";
    const modificationHistoryCount = Number(
        booking?.modificationHistoryCount ||
        booking?.modificationRequestCount ||
        booking?.requestChangeBookingCount ||
        booking?.requestChangeBookings?.length ||
        0
    );
    const isCancelledBooking = String(booking?.bookingStatus || "").toUpperCase() === "CANCELLED";
    const hasDraft = (
        Array.isArray(booking?.requestChangeBookings)
            ? booking.requestChangeBookings
            : Array.isArray(booking?.requestChangeBooking)
            ? booking.requestChangeBooking
            : []
    ).some(item => String(item?.status || "").toUpperCase() === "DRAFT");
    const currentJST = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const isPastPickup = booking?.pickupDateTime && currentJST > new Date(booking.pickupDateTime);
    // Hides the whole admin edit-booking entry point: the edit link, the modification
    // history link, and the draft badge, which only exists because of that feature.
    const showAdminEditBooking = process.env.REACT_APP_EDIT_BOOKING_ADMIN === "true";


    const formatBookingDetailDateTime = (input) => {
        if (!input) return "-";
        return moment(input).locale("en").format("MMM. Do, YYYY (ddd) HH:mm");
    };

    const formatDateTimeCard = (input) => {
        if (!input) return "-";
        const date = new Date(input);
        if (Number.isNaN(date.getTime())) return "-";
        const month = date.toLocaleString("en-US", { month: "short" });
        const day = date.getDate();
        const year = date.getFullYear();
        const hour = String(date.getHours()).padStart(2, "0");
        const minute = String(date.getMinutes()).padStart(2, "0");
        return `${month} ${day}, ${year} ${hour}:${minute}`;
    };

    const formatTransferType = (booking) => {
        if (booking?.bookingType === "byTheHour") return "Charter / By The Hour";
        if (booking?.isAirportTransfer) return "Airport Transfer";
        return "Specified Destination";
    };

    const formatDurationSimple = (minutesValue) => {
        const totalMinutes = Number(minutesValue || 0);
        if (!totalMinutes) return "-";
        const rounded = Math.ceil((totalMinutes / 60) * 2) / 2;
        return `${rounded} Hours`;
    };

    const formatDuration = (minutesValue) => {
        const totalMinutes = Number(minutesValue || 0);
        if (!totalMinutes) return "-";
        const rounded = Math.ceil((totalMinutes / 60) * 2) / 2;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${rounded} Hours (${hours}h${minutes > 0 ? `${minutes}m` : ""})`;
    };

    const formatDateOnly = (input) => {
        if (!input) return "-";
        const date = new Date(input);
        if (Number.isNaN(date.getTime())) return "-";
        const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}/${month}/${day} (${weekdays[date.getDay()]})`;
    };

    const formatDistanceKm = (distanceMeter) => {
        const meter = Number(distanceMeter || 0);
        if (!meter) return "-";
        const km = meter / 1000;
        return `${km.toFixed(2)} KM`;
    };

    const formatTravelAreaLabel = (travelAreaValue) => {
        const value = String(travelAreaValue || "");
        if (value.includes("neighboringPerfecture")) return "To the neighboring city/prefecture";
        if (value.includes("withinCity")) return "Within city";
        if (value.includes("undecided")) return "Undecided";
        return travelAreaValue || "-";
    };

    const formatTerminalLabel = (terminalValue) => {
        if (!terminalValue) return "-";
        const normalized = String(terminalValue).trim().toLowerCase();
        return TERMINAL_LABEL_MAP[normalized] || terminalValue;
    };


    const getRoleText = (role, isDeferred) => {
        if (role === "individual") {
            return "B2C"
        } else if (role === "business") {
            if (isDeferred) {
                return "B2B (Deferred)";
            } else {
                return "B2B (Advance)";
            }
        } else {
            return "Guest"
        }
    }

    return (
        <>
            <SectionTitle title="Booking Details" />
            <div className="border rounded mb-3">
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Last Updated" value={formatDateTimeCard(booking?.updatedAt)} />
                        <DetailItem label="Booking Date" value={formatDateTimeCard(booking?.date)} />
                        <DetailItem
                            label="User Type"
                            value={
                                getRoleText(booking?.customer?.user?.role, booking?.customer?.user?.deferredPaymentEligible)
                            }
                        />
                        <DetailItem label="Booking Channel" value={booking?.fromQuotation ? "Admin (Quotation)" : "Website"} />
                        <DetailItem label="Discount" value={booking?.discount?.rate ? `${booking?.discount?.rate || 0}%` : "Not Applicable"} />
                    </div>
                </div>
                <hr className="hr-E8E8E8 m-0" />
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <div>
                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Passengers</div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400]">
                                <span style={{ color: countColor(adultCount) }}>{`Adult: ${adultCount} / `}</span>
                                <span style={{ color: countColor(childCount) }}>{`Child: ${childCount} /`}</span>
                            </div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400]">
                                <span style={{ color: countColor(toddlerCount) }}>{`Toddler: ${toddlerCount} / `}</span>
                                <span style={{ color: countColor(infantCount) }}>{`Infant: ${infantCount} `}</span>
                            </div>
                        </div>
                        <div>
                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Luggage</div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400]" style={{ color: countColor(checkinCount) }}>
                                {`Check-in Size: ${checkinCount}`}
                            </div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400]" style={{ color: countColor(cabinCount) }}>
                                {`Cabin Size: ${cabinCount}`}
                            </div>
                        </div>
                        <div>
                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Special Request</div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{specialRequestText}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="border rounded mb-3">
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Type of Transfer" value={formatTransferType(booking)} />
                        <DetailItem label="Usage Duration" value={formatDurationSimple(booking?.usageDuration)} />
                        <DetailItem label="Billed Duration" value={formatDuration(booking?.billedDuration)} />
                    </div>
                </div>
                <hr className="hr-E8E8E8 m-0" />
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Travel Date" value={formatDateOnly(booking?.travelDate)} />
                        <DetailItem label="Total Distance" value={formatDistanceKm(booking?.totalDistance)} />
                        <DetailItem label="Travel Area" value={formatTravelAreaLabel(booking?.travelArea)} />
                    </div>
                </div>
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Pick-up Time" value={formatBookingDetailDateTime(booking?.pickupDateTime)} />
                        <div className="tw:md:col-span-3">
                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Pick-up Location</div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{pickupLocationBlock.title}</div>
                            {pickupLocationBlock.subtitle ? (
                                <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#7C7B7B]">{pickupLocationBlock.subtitle}</div>
                            ) : null}
                        </div>
                        <DetailItem label="Garage Dispatch Time" value={formatBookingDetailDateTime(booking?.startFromGarage)} />
                    </div>
                </div>
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Estimated Drop-off Time" value={formatBookingDetailDateTime(booking?.customerDropoffDateTime)} />
                        <div className="tw:md:col-span-3">
                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Drop-off Location</div>
                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{dropoffLocationBlock.title}</div>
                            {dropoffLocationBlock.subtitle ? (
                                <div className="tw:!font-hanken-grotesk tw:!text-[13px] tw:!font-[400] tw:!text-[#7C7B7B]">{dropoffLocationBlock.subtitle}</div>
                            ) : null}
                        </div>
                        <DetailItem label="Garage Arrival Time" value={formatBookingDetailDateTime(booking?.garageArivalTime || booking?.garageArivalTimeWithAdditionalTime)} />
                    </div>
                </div>
                <hr className="hr-E8E8E8 m-0" />
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Package" value={booking?.package?.name || "-"} />
                        <DetailItem label="Pick-up Flight No." value={booking?.pickupFlightNo || "-"} />
                        <DetailItem label="Arrival Terminal" value={formatTerminalLabel(booking?.pickupTerminal)} />
                        <DetailItem label="Drop-off Flight No." value={booking?.dropoffFlightNo || "-"} />
                        <DetailItem label="Departure Terminal" value={formatTerminalLabel(booking?.dropoffTerminal)} />
                    </div>
                </div>
                <div className="d-flex align-items-start gap-5 p-3">
                    <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                        <DetailItem label="Meet & Assist Service" value={(booking?.meetAssist || [])
                            .filter((item) => item?.meetAssistName && item?.meetAssistName !== "none")
                            .map((item) => `${MEET_ASSIST_LABELS[item?.meetAssistName] || item?.meetAssistName} (${item?.meetAssistType === "arrival" ? "Arrival" : "Departure"})`)
                            .join(", ") || "-"}
                        />
                        <DetailItem label="Airport Pick Up Sign" value={booking?.pickupSign || "-"} />
                    </div>
                </div>
                <hr className="hr-E8E8E8 m-0" />
                <div className="d-flex align-items-start gap-5 p-3">
                    {isMultipleVehicle ? (
                        <div className="tw:w-full tw:flex tw:flex-col">
                            {multiVehicleAddonRows.map((row, index) => (
                                <div key={row.key} className={cn(index > 0 && "tw:pt-6", index < multiVehicleAddonRows.length - 1 && "tw:pb-6")}>
                                    <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-[340px_1px_1fr_1fr_1fr] tw:md:items-center">
                                        <div>
                                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">{row.vehicleLabel}</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{row.vehicleName}</div>
                                        </div>
                                        <div className="tw:hidden tw:md:block tw:h-full tw:w-px tw:bg-[#E8E8E8]" />
                                        <div>
                                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Child Seats & Booster Seats</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">
                                                {row.childSeatText === "-" ? "-" : row.childSeatText.split(" | ").map((line, lineIndex) => (
                                                    <div key={`${row.key}-seat-${lineIndex}`}>{line}</div>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Baby Stroller</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{row.babyStroller}</div>
                                        </div>
                                        <div>
                                            <div className="tw:!font-hanken-grotesk tw:!m-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#8D8D8D]">Wheelchair</div>
                                            <div className="tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828]">{row.wheelchair}</div>
                                        </div>
                                    </div>
                                    {index < multiVehicleAddonRows.length - 1 ? <div className="tw:mt-6 tw:h-px tw:w-full tw:bg-[#E8E8E8]" /> : null}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-12 tw:gap-y-3 tw:md:grid-cols-5">
                            <DetailItem label="Child Seats & Booster Seats" value={childSeatSummary || "-"} />
                            <DetailItem label="Baby Stroller" value={booking?.babyStroller || "-"} />
                            <DetailItem label="Wheelchair" value={booking?.wheelchair || "-"} />
                        </div>
                    )}
                </div>
            </div>

            {showAdminEditBooking && (
            <div className="d-flex align-items-start gap-2 tw:!pb-6">
                {!isCancelledBooking && (
                    isPastPickup ? (
                        <span className="tw:inline-flex tw:cursor-not-allowed tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:opacity-50">
                            <span className="tw:!font-hanken-grotesk tw:!font-[400] tw:text-[14px] tw:text-[#282828]">Edit Booking Details</span>
                        </span>
                    ) : (
                        <Link
                            to={`/admin/booking-edit?bookingId=${booking?._id || ""}`}
                            className="tw:inline-flex tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
                        >
                            <span className="tw:!font-hanken-grotesk tw:!font-[400] tw:text-[14px] tw:text-[#282828]">Edit Booking Details</span>
                        </Link>
                    )
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                    {modificationHistoryCount > 0 ? (
                        <Link
                            to={`/admin/bookinglist/edit/v2/${booking?._id}/modification-history`}
                            className="tw:!no-underline"
                        >
                            <span className="tw:!font-hanken-grotesk tw:!font-[400] tw:text-[14px] tw:inline-flex tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:px-3 tw:py-2 tw:text-[#282828] tw:bg-[#F1F1F1]">
                                {`Modification History (${modificationHistoryCount})`}
                            </span>
                        </Link>
                    ) : (
                        <span className="tw:!font-hanken-grotesk tw:!font-[400] tw:text-[14px] tw:inline-flex tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:px-3 tw:py-2 tw:text-[#8d8d8d] tw:bg-[#F9F9F9] tw:opacity-50 tw:cursor-not-allowed">
                            {`Modification History (${modificationHistoryCount})`}
                        </span>
                    )}
                    {hasDraft && (
                        <span className="tw:!font-hanken-grotesk tw:!font-[400] tw:text-[14px] tw:inline-flex tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:border tw:border-[#A3780F] tw:px-3 tw:py-2 tw:text-[#A3780F] tw:bg-[#FFF8E8]">
                            Draft Pending
                        </span>
                    )}
                </div>
            </div>
            )}
        </>
    );
};

export default BookingDetails;
